import type { PricePoint } from "~/lib/api";
import { formatPrice } from "~/lib/format";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { Input } from "~/components/ui/input";
import { useInlineEditor } from "~/hooks/useInlineEditor";
import { parsePricePoint } from "./currencies";
import { formatPricePointLabel } from "./pricing-types";

interface PricePointEditorProps {
  value: PricePoint;
  currency: string;
  onChange: (value: PricePoint) => void;
  canEdit?: boolean;
  initialEdit?: boolean;
  onCancel?: () => void;
}

export function PricePointEditor({ value, currency, onChange, canEdit, initialEdit, onCancel }: PricePointEditorProps) {
  const {
    editing,
    saving,
    error,
    editState,
    containerRef,
    primaryInputRef,
    startEditing,
    save,
    handleKeyDown,
    handleBlur,
    setField,
  } = useInlineEditor<PricePoint>({
    value,
    onSave: (v) => {
      if (v) onChange(v);
    },
    toEditState: (v) => ({
      amount: v?.amount ? v.amount.toString() : "",
      label: v?.label ?? "",
    }),
    fromEditState: (state) => {
      const point = parsePricePoint(state, value.type);
      if (!point) throw new Error("Enter a valid price");
      return point;
    },
    initialEdit,
    onCancel,
  });

  if (editing) {
    return (
      <div ref={containerRef} onBlur={handleBlur}>
        <div className="flex gap-2 items-center">
          <Input
            ref={primaryInputRef}
            type="number"
            min="0"
            value={editState.amount}
            onChange={(e) => setField("amount", e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Price"
            disabled={saving}
            aria-invalid={!!error}
            className="w-24 h-8 text-sm"
            aria-label="Amount"
          />
          <Input
            type="text"
            value={editState.label}
            onChange={(e) => setField("label", e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Description (optional)"
            disabled={saving}
            className="flex-1 h-8 text-sm"
            aria-label="Label"
          />
          <Button
            size="icon-sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={save}
            disabled={saving}
            aria-label="Save"
          >
            <Icon i="check" className="size-6 text-primary" />
          </Button>
        </div>
        {error && <p className="text-xs text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  const displayLabel = formatPricePointLabel(value);
  const formatted = formatPrice(value.amount, currency);
  const content = (
    <>
      <span className="text-muted-foreground">{displayLabel}</span>
      <span className="font-medium tabular-nums">{formatted}</span>
    </>
  );

  if (canEdit) {
    return (
      <button
        type="button"
        onClick={startEditing}
        className="flex flex-1 items-baseline justify-between text-sm text-foreground hover:text-primary transition-colors"
      >
        {content}
      </button>
    );
  }

  return <div className="flex flex-1 items-baseline justify-between text-sm">{content}</div>;
}
